'use client'

import { useEffect, useRef, useState } from 'react'
import { cn } from '~/lib/utils'
import { FeatureCard } from './feature-card'

const SYSTEM_LOGS = [
  'Поиск новых вакансий',
  'Анализ описания',
  'Проверка фильтров',
  'Загрузка профиля',
  'Обновление токена',
  'Синхронизация',
  'Парсинг страницы',
  'Проверка дубликатов',
  'Оценка релевантности',
  'Сохранение в базу',
  'Логирование событий',
  'Очистка кэша',
  'Валидация сессии',
  'Генерация эмбеддингов',
  'Подсчет скоринга',
  'Мониторинг статусов',
  'Обновление резюме',
]

const ACTIVE_ACTIONS = [
  'Отклик на вакансию',
  'Генерация сопроводительного',
  'Отправка резюме',
  'Получено приглашение',
  'Назначено интервью',
  'Новое сообщение от HR',
  'Успешный отклик',
  'Подготовка ответов',
]

const COMPANIES = [
  'Яндекс',
  'Сбер',
  'Авито',
  'Ozon',
  'Т-Банк',
  'VK',
  'Лаборатория Касперского',
  'Альфа-Банк',
  'Wildberries',
  'МТС',
  'Билайн',
  'Мегафон',
  'Самокат',
  'Ростелеком',
  'Газпромбанк',
  'X5 Tech',
  'Lamoda',
  'Циан',
  'Додо Пицца',
  'Skyeng',
  'Positive Technologies',
  'Selectel',
  '2ГИС',
  'РЖД',
  'Аэрофлот',
]

const VACANCIES = [
  'Frontend Developer',
  'Backend Developer',
  'DevOps Engineer',
  'QA Automation',
  'Fullstack Developer',
  'System Analyst',
  'Product Manager',
  'Team Lead',
  'Data Scientist',
]

interface LogLine {
  id: string
  text: string
  type: 'system' | 'active'
  opacity: number
}

interface AsciiSearchArtProps {
  className?: string
}

function AsciiSearchArt(props: AsciiSearchArtProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [lines, setLines] = useState<LogLine[]>([])
  const [maxLines, setMaxLines] = useState(0)

  useEffect(() => {
    if (!containerRef.current)
      return

    const updateDimensions = () => {
      if (!containerRef.current)
        return
      const { offsetHeight } = containerRef.current
      setMaxLines(Math.floor(offsetHeight / 20) + 1)
    }

    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  const generateLine = (): LogLine => {
    const isActive = Math.random() < 0.2
    const id = Math.floor(Math.random() * 0xFFFF).toString(16).padStart(4, '0').toUpperCase()

    let text = ''

    if (isActive) {
      const action = ACTIVE_ACTIONS[Math.floor(Math.random() * ACTIVE_ACTIONS.length)]
      const company = COMPANIES[Math.floor(Math.random() * COMPANIES.length)]
      text = `[${action}] → ${company}`
    }
    else {
      const log = SYSTEM_LOGS[Math.floor(Math.random() * SYSTEM_LOGS.length)]
      const vacancy = VACANCIES[Math.floor(Math.random() * VACANCIES.length)]
      const processId = Math.floor(Math.random() * 900) + 100
      text = `[System] ${log}: ${vacancy} (PID:${processId})`
    }

    return {
      id,
      text,
      type: isActive ? 'active' : 'system',
      opacity: 0,
    }
  }

  useEffect(() => {
    if (maxLines === 0)
      return

    const initialLines = []
    for (let i = 0; i < maxLines; i++) {
      initialLines.push(generateLine())
    }
    setLines(initialLines)

    const interval = setInterval(() => {
      setLines((prev) => {
        const newLines = [...prev.slice(1), generateLine()]
        return newLines
      })
    }, 200)

    return () => clearInterval(interval)
  }, [maxLines])

  return (
    <div
      ref={containerRef}
      className={cn(
        'pointer-events-none size-full overflow-hidden font-mono text-xs leading-5',
        props.className,
      )}
      aria-hidden="true"
    >
      <div className="flex h-full flex-col justify-end">
        {lines.map((line, i) => (
          <div
            key={`${line.id}-${i}`}
            className={`flex items-center gap-3 px-4 whitespace-nowrap transition-colors duration-300 ${
              line.type === 'active'
                ? 'bg-orange-500/5 font-medium text-orange-500/90'
                : 'text-gray-400/30'
            }`}
          >
            <span className="w-10 shrink-0 text-[10px] opacity-80">
              0x
              {line.id}
            </span>
            <span className={`w-16 shrink-0 ${line.type === 'active' ? 'opacity-100' : 'opacity-80'}`}>
              {line.type === 'active' ? '[ACTION]' : '[LOG]'}
            </span>
            <span className="truncate">{line.text}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

interface CareerAutopilotProps {
  className?: string
}

export function CareerAutopilot(props: CareerAutopilotProps) {
  return (
    <FeatureCard
      background={<AsciiSearchArt />}
      title="Карьерный автопилот"
      description="Система сама находит вакансии, пишет персонализированные отклики и ведёт переписку с рекрутерами. Учитывает ключевые редфлаги при общении с HR, избегает шаблонности, клише и характерного AI-стиля. Пишет по-человечески, простым и естественным языком, чтобы вызвать максимальное доверие и не создавать ощущения автоматизации. Вам остаётся только выбрать удобное время для собеседования."
      className={props.className}
    />
  )
}
